// Janelinha flutuante de chat 1:1 entre alunos — estilo LinkedIn.
// [[project-peer-chat-floating-and-hub]] Fase 1.
//
// Comportamento:
//   - Empilha 1-3 janelas no canto inferior direito (a 4ª empurra a + antiga).
//   - Cada janela pode ser minimizada pra "pílula" só com nome+unread.
//   - Polling 8s por janela aberta.
//   - Inicia conversas via `openChatWith(peerUserId, peerName)`.

import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  ChevronDown,
  Loader2,
  Maximize2,
  MessageCircle,
  Send,
  X
} from "lucide-react";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { getInitials } from "@app/ui/lib/display-name";
import { prefs, setActiveNav } from "./App";
import { parseShareBody, ShareBubble } from "./PeerChatShareBubble";
import { openPeerChatHubWithThread } from "./PeerChatHubView";

interface DockWindow {
  threadId: string | null;
  peerUserId: string;
  peerName: string | null;
  minimized: boolean;
}

interface ChatMessage {
  id: string;
  senderId: string;
  body: string;
  createdAt: string;
  isMine: boolean;
  readByRecipientAt: string | null;
}

const MAX_OPEN = 3;
const POLL_MS = 8000;

const openWindows = signal<DockWindow[]>([]);
// peerIds que estão saindo (animação de exit). Mantemos a janela no DOM
// até a animação terminar, depois filtramos do `openWindows`.
const closingIds = signal<Set<string>>(new Set());
const EXIT_MS = 220;

// =====================
// Auto-open poller — escuta /chat/threads e, quando uma mensagem nova chega
// de uma conexão, abre a janelinha automaticamente se o aluno habilitou
// `peerChatAutoOpen` em Preferências. Polling lento (15s) pra não onerar
// quando a feature está desligada. [[project-peer-chat-autoopen-toggle]]
// =====================
interface ThreadSnapshot {
  unreadCount: number;
  lastMessageAt: string | null;
}
type ThreadRowApi = {
  id: string;
  peer: { id: string; name: string | null };
  lastMessageAt: string | null;
  unreadCount: number;
};

const AUTOOPEN_POLL_MS = 15000;
const seenThreads = new Map<string, ThreadSnapshot>();
// Inicializado depois do primeiro fetch — sem isso, ao ligar a feature
// quando já existem mensagens não-lidas antigas, o dock abriria tudo.
let seenInitialized = false;

function useAutoOpenPoller() {
  const enabledRef = useRef<boolean>(prefs.value.peerChatAutoOpen);
  useEffect(() => {
    enabledRef.current = prefs.value.peerChatAutoOpen;
  }, [prefs.value.peerChatAutoOpen]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      try {
        const res = await call<{ threads: ThreadRowApi[] }>(
          "/api/student/chat/threads"
        );
        if (cancelled) return;
        const wantsAutoOpen = enabledRef.current;
        for (const t of res.threads) {
          const prev = seenThreads.get(t.id);
          const ts = t.lastMessageAt;
          // Baseline: na primeira passada, só registra; não dispara.
          if (!seenInitialized) {
            seenThreads.set(t.id, { unreadCount: t.unreadCount, lastMessageAt: ts });
            continue;
          }
          // Detecta mensagem nova: unread aumentou OU lastMessageAt avançou
          // com unread > 0 (cobre o caso de quem leu e voltou a receber).
          const grew = !prev || t.unreadCount > prev.unreadCount;
          const newer =
            prev &&
            t.unreadCount > 0 &&
            ts &&
            (!prev.lastMessageAt || Date.parse(ts) > Date.parse(prev.lastMessageAt));
          if (wantsAutoOpen && (grew || newer)) {
            // Não abre se já existe janela visível pra esse peer.
            const already = openWindows.value.some(
              (w) => w.peerUserId === t.peer.id
            );
            if (!already) {
              openChatWith(t.peer.id, t.peer.name);
            }
          }
          seenThreads.set(t.id, {
            unreadCount: t.unreadCount,
            lastMessageAt: ts
          });
        }
        seenInitialized = true;
      } catch {
        /* best-effort, silencioso */
      } finally {
        if (!cancelled) timer = setTimeout(tick, AUTOOPEN_POLL_MS);
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);
}

export function openChatWith(peerUserId: string, peerName: string | null) {
  const list = openWindows.value;
  // Já aberto? Só desminimiza.
  const existingIdx = list.findIndex((w) => w.peerUserId === peerUserId);
  if (existingIdx >= 0) {
    const next = [...list];
    next[existingIdx] = { ...next[existingIdx], minimized: false };
    openWindows.value = next;
    return;
  }
  // Novo: cria stub (thread será garantida no useEffect da janela).
  const next: DockWindow[] = [
    ...list,
    { threadId: null, peerUserId, peerName, minimized: false }
  ];
  // Mantém só os MAX_OPEN mais novos.
  if (next.length > MAX_OPEN) next.splice(0, next.length - MAX_OPEN);
  openWindows.value = next;
}

function closeWindow(peerUserId: string) {
  // Marca como "closing" pra rodar animação de exit, remove depois.
  const next = new Set(closingIds.value);
  next.add(peerUserId);
  closingIds.value = next;
  setTimeout(() => {
    openWindows.value = openWindows.value.filter((w) => w.peerUserId !== peerUserId);
    const cleaned = new Set(closingIds.value);
    cleaned.delete(peerUserId);
    closingIds.value = cleaned;
  }, EXIT_MS);
}

function toggleMinimize(peerUserId: string) {
  openWindows.value = openWindows.value.map((w) =>
    w.peerUserId === peerUserId ? { ...w, minimized: !w.minimized } : w
  );
}

export function PeerChatDock() {
  // Roda o poller global ANTES do early-return — regra de hooks.
  // [[project-peer-chat-autoopen-toggle]]
  useAutoOpenPoller();
  const list = openWindows.value;
  const closing = closingIds.value;
  if (list.length === 0) return null;
  return (
    <div class="pointer-events-none fixed bottom-0 right-20 z-30 flex items-end gap-3">
      {list.map((w) => {
        const isClosing = closing.has(w.peerUserId);
        return (
          <div
            key={w.peerUserId}
            data-state={isClosing ? "closed" : "open"}
            class="pointer-events-auto origin-bottom-right transition-[transform,opacity] duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-bottom-2 data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:slide-in-from-bottom-3 data-[state=open]:zoom-in-95"
            style={{ animationDuration: "220ms", animationFillMode: "both" }}
          >
            <ChatWindow w={w} />
          </div>
        );
      })}
    </div>
  );
}

function ChatWindow({ w }: { w: DockWindow }) {
  const [threadId, setThreadId] = useState<string | null>(w.threadId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Get-or-create thread quando a janela monta.
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        if (!threadId) {
          const res = await call<{ thread: { id: string } }>(
            "/api/student/chat/threads",
            { method: "POST", body: JSON.stringify({ peerUserId: w.peerUserId }) }
          );
          if (cancelled) return;
          setThreadId(res.thread.id);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(friendlyError(e));
          setPhase("error");
        }
      }
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, [w.peerUserId]);

  // Load + poll messages.
  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      try {
        const res = await call<{ messages: ChatMessage[] }>(
          `/api/student/chat/messages?threadId=${encodeURIComponent(threadId!)}&limit=50`
        );
        if (cancelled) return;
        // Backend devolve do mais recente pro mais antigo — invertemos.
        const ordered = [...res.messages].reverse();
        setMessages(ordered);
        const u = ordered.filter(
          (m) => !m.isMine && !m.readByRecipientAt
        ).length;
        setUnread(u);
        setPhase("ready");

        // Se janela está aberta (não minimizada), marca como lidas.
        if (!w.minimized && u > 0) {
          void call(
            `/api/student/chat/messages?threadId=${encodeURIComponent(threadId!)}`,
            { method: "PATCH" }
          ).catch(() => {/* best-effort */});
        }
      } catch (e) {
        if (!cancelled) {
          setErr(friendlyError(e));
          setPhase("error");
        }
      } finally {
        if (!cancelled) timer = setTimeout(tick, POLL_MS);
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [threadId, w.minimized]);

  // Auto-scroll para o fim quando msgs mudam (somente se já estamos no fim).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Heurística: se distância do bottom < 80px, scrolla.
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (dist < 80) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  async function send() {
    if (!threadId || !input.trim() || sending) return;
    const body = input.trim();
    setSending(true);
    setInput("");
    try {
      const res = await call<{ message: ChatMessage }>(
        `/api/student/chat/messages?threadId=${encodeURIComponent(threadId!)}`,
        {
          method: "POST",
          body: JSON.stringify({ body })
        }
      );
      setMessages((prev) => [...prev, res.message]);
      setErr(null);
    } catch (e) {
      setErr(friendlyError(e));
      setInput(body); // restaura
    } finally {
      setSending(false);
    }
  }

  const initials = getInitials({
    name: w.peerName ?? "",
    socialName: null,
    initialsOverride: null
  });

  if (w.minimized) {
    return (
      <button
        type="button"
        onClick={() => toggleMinimize(w.peerUserId)}
        className="mb-0 flex items-center gap-2 rounded-t-md border border-border bg-card px-3 py-2 text-sm font-semibold shadow-lg hover:bg-accent"
        title="Abrir conversa"
      >
        <span class="grid size-7 place-items-center rounded-full bg-primary/15 text-[10px] font-bold uppercase text-primary">
          {initials}
        </span>
        <span class="max-w-[140px] truncate">{w.peerName ?? "Conversa"}</span>
        {unread > 0 && (
          <span class="grid size-5 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
            {unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div class="flex h-96 w-80 flex-col overflow-hidden rounded-t-lg border border-border bg-card shadow-2xl">
      <header class="flex shrink-0 items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <span class="grid size-7 place-items-center rounded-full bg-primary/15 text-[10px] font-bold uppercase text-primary">
          {initials}
        </span>
        <span class="min-w-0 flex-1 truncate text-sm font-bold">
          {w.peerName ?? "Conversa"}
        </span>
        <button
          type="button"
          onClick={() => {
            // Abre o hub do Chat com essa conversa selecionada e
            // minimiza a janelinha automaticamente — UX 2026-05-23.
            openPeerChatHubWithThread(threadId);
            setActiveNav("peerchat");
            toggleMinimize(w.peerUserId);
          }}
          disabled={!threadId}
          aria-label="Abrir no Chat"
          title="Abrir no Chat (tela cheia)"
          class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent disabled:opacity-50"
        >
          <Maximize2 className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => toggleMinimize(w.peerUserId)}
          aria-label="Minimizar"
          title="Minimizar"
          class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent"
        >
          <ChevronDown className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => closeWindow(w.peerUserId)}
          aria-label="Fechar"
          title="Fechar"
          class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent"
        >
          <X className="size-4" />
        </button>
      </header>

      <div ref={scrollRef} class="flex-1 space-y-2 overflow-y-auto bg-background/40 p-3">
        {phase === "loading" && (
          <p class="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" /> Carregando…
          </p>
        )}
        {phase === "error" && (
          <p class="text-xs text-destructive">{err}</p>
        )}
        {phase === "ready" && messages.length === 0 && (
          <p class="px-2 py-6 text-center text-xs text-muted-foreground">
            <MessageCircle className="mx-auto mb-2 size-6 text-muted-foreground/50" />
            Diga oi — esta é a primeira conversa entre vocês.
          </p>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
        class="flex shrink-0 items-end gap-1.5 border-t border-border bg-card px-2 py-2"
      >
        <textarea
          value={input}
          onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          rows={1}
          maxLength={4000}
          placeholder="Mensagem…"
          aria-label="Mensagem"
          class="max-h-24 flex-1 resize-none rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-primary"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          aria-label="Enviar"
          title="Enviar (Enter)"
          className="grid size-8 place-items-center rounded-md bg-primary text-primary-foreground hover:brightness-110 disabled:opacity-40"
        >
          {sending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
        </button>
      </form>
    </div>
  );
}

function Bubble({ m }: { m: ChatMessage }) {
  const share = parseShareBody(m.body);
  if (share) {
    return <ShareBubble payload={share} mine={m.isMine} />;
  }
  if (m.isMine) {
    return (
      <div class="flex justify-end">
        <div class="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-3 py-1.5 text-xs text-primary-foreground">
          <pre class="whitespace-pre-wrap break-words font-sans">{m.body}</pre>
        </div>
      </div>
    );
  }
  return (
    <div class="flex justify-start">
      <div class="max-w-[80%] rounded-2xl rounded-bl-sm border border-border bg-card px-3 py-1.5 text-xs">
        <pre class="whitespace-pre-wrap break-words font-sans">{m.body}</pre>
      </div>
    </div>
  );
}
